import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Avatar from "@/components/chat/Avatar";
import ThemeToggle from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { relativeSeen } from "@/lib/format";
import { ArrowLeft, Copy, Ticket, Trash2, ShieldCheck, Loader2, Check, Download, Server, Save, PlayCircle, Upload, KeyRound } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = { attivo: "bg-green-500/15 text-green-600", usato: "bg-slate-500/15 text-slate-500", scaduto: "bg-orange-500/15 text-orange-600", revocato: "bg-red-500/15 text-red-600" };

export default function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState("");
  const [days, setDays] = useState(7);
  const [generating, setGenerating] = useState(false);
  const [lastCode, setLastCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [confirmUser, setConfirmUser] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [resetPinUser, setResetPinUser] = useState(null);
  const [resettingPin, setResettingPin] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [ftp, setFtp] = useState(null);
  const [savingFtp, setSavingFtp] = useState(false);
  const [runningFtp, setRunningFtp] = useState(false);
  const [sec, setSec] = useState({ idleMinutes: 5, backgroundSeconds: 15 });
  const [savingSec, setSavingSec] = useState(false);

  const load = useCallback(async () => {
    const [u, i, f, s] = await Promise.all([
      api.get("/users"), api.get("/invites"), api.get("/admin/ftp-config"), api.get("/security-settings"),
    ]);
    setUsers(u.data);
    setInvites(i.data);
    setFtp({ ...f.data, password: "" });
    setSec(s.data);
  }, []);

  const saveSec = async () => {
    setSavingSec(true);
    try {
      await api.put("/security-settings", {
        idleMinutes: Number(sec.idleMinutes) || 5,
        backgroundSeconds: Number(sec.backgroundSeconds) || 15,
        requirePin: !!sec.requirePin,
      });
      toast.success("Impostazioni di sicurezza salvate");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSavingSec(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}/active`, { active: !u.active });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, active: !x.active } : x)));
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/invites", { email: email.trim() || null, expiresInDays: Number(days) });
      setLastCode(data.code);
      setEmail("");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setGenerating(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/invites/${id}`);
      load();
    } catch (e) {
      toast.error("Revoca non riuscita");
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(lastCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const downloadBackup = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/admin/backup", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : "backup_relatio.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Backup scaricato");
    } catch (e) {
      toast.error("Backup non riuscito");
    } finally {
      setDownloading(false);
    }
  };

  const doDelete = async () => {
    if (!confirmUser) return;
    setDeleting(true);
    try {
      await api.delete(`/users/${confirmUser.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== confirmUser.id));
      toast.success("Utente eliminato");
      setConfirmUser(null);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const doResetPin = async () => {
    if (!resetPinUser) return;
    setResettingPin(true);
    try {
      await api.delete(`/users/${resetPinUser.id}/pin`);
      setUsers((prev) => prev.map((x) => (x.id === resetPinUser.id ? { ...x, pinSet: false } : x)));
      toast.success("PIN azzerato");
      setResetPinUser(null);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setResettingPin(false);
    }
  };

  const setFtpField = (k, v) => setFtp((prev) => ({ ...prev, [k]: v }));

  const saveFtp = async () => {
    setSavingFtp(true);
    try {
      const body = {
        host: ftp.host || "", port: Number(ftp.port) || 21, username: ftp.username || "",
        remoteDir: ftp.remoteDir || "", tls: !!ftp.tls, enabled: !!ftp.enabled,
        hour: Number(ftp.hour) || 0,
      };
      if (ftp.password) body.password = ftp.password;
      await api.put("/admin/ftp-config", body);
      toast.success("Configurazione FTP salvata");
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSavingFtp(false);
    }
  };

  const runFtp = async () => {
    setRunningFtp(true);
    try {
      const { data } = await api.post("/admin/ftp-backup/run");
      toast.success(`Backup caricato: ${data.file}`);
      await load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setRunningFtp(false);
    }
  };

  const [restoring, setRestoring] = useState(false);
  const doRestore = async () => {
    const file = restoreFile;
    if (!file) return;
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const r = data.restored || {};
      toast.success(`Ripristino completato: ${r.conversations || 0} schede, ${r.messages || 0} note`);
      setRestoreFile(null);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="h-[100dvh] overflow-y-auto bg-background" data-testid="admin-panel-scroll">
      <header className="px-4 py-3 flex items-center gap-3 border-b border-border sticky top-0 bg-background/90 backdrop-blur-md z-20">
        <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-button"><ArrowLeft className="h-5 w-5" /></Button>
        <div className="flex items-center gap-2 flex-1">
          <ShieldCheck className="h-5 w-5 text-sky-500" />
          <h1 className="font-bold text-lg">Pannello Amministrazione</h1>
        </div>
        <Button variant="outline" size="sm" onClick={downloadBackup} disabled={downloading} data-testid="backup-download-button" className="gap-2">
          {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          <span className="hidden sm:inline">Backup ZIP</span>
        </Button>
        <ThemeToggle />
      </header>

      <div className="max-w-3xl mx-auto p-4 md:p-8">
        <Tabs defaultValue="users">
          <TabsList className="grid grid-cols-4 w-full max-w-lg mb-6">
            <TabsTrigger value="users" data-testid="admin-tab-users">Utenti</TabsTrigger>
            <TabsTrigger value="invites" data-testid="admin-tab-invites">Inviti</TabsTrigger>
            <TabsTrigger value="backup" data-testid="admin-tab-backup">Backup</TabsTrigger>
            <TabsTrigger value="security" data-testid="admin-tab-security">Sicurezza</TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="space-y-2">
            {users.map((u) => (
              <div key={u.id} data-testid={`admin-user-${u.id}`} className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card">
                <Avatar name={u.displayName} isGroup={false} showStatus={false} size={44} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm truncate">{u.displayName}</span>
                    {u.role === "admin" && <Badge className="bg-sky-500/15 text-sky-600 hover:bg-sky-500/15">admin</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{u.email} · visto {relativeSeen(u.lastSeen)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground hidden sm:block">{u.active ? "Attivo" : "Disattivo"}</Label>
                  <Switch checked={u.active} disabled={u.id === user.id} onCheckedChange={() => toggleActive(u)} data-testid={`toggle-active-${u.id}`} />
                  {u.pinSet && (
                    <Button size="icon" variant="ghost" className="text-amber-600" title="Reset PIN" onClick={() => setResetPinUser(u)} data-testid={`reset-pin-${u.id}`}>
                      <KeyRound className="h-4 w-4" />
                    </Button>
                  )}
                  <Button size="icon" variant="ghost" className="text-destructive" disabled={u.id === user.id} onClick={() => setConfirmUser(u)} data-testid={`delete-user-${u.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="invites" className="space-y-6">
            <div className="p-4 rounded-xl border border-border bg-card space-y-4">
              <h3 className="font-semibold flex items-center gap-2"><Ticket className="h-4 w-4" /> Genera nuovo invito</h3>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 space-y-1.5">
                  <Label className="text-xs">Email (facoltativa)</Label>
                  <Input placeholder="collega@azienda.it" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email-input" />
                </div>
                <div className="w-full sm:w-32 space-y-1.5">
                  <Label className="text-xs">Scadenza (giorni)</Label>
                  <Input type="number" min={1} max={90} value={days} onChange={(e) => setDays(e.target.value)} data-testid="invite-days-input" />
                </div>
              </div>
              <Button onClick={generate} disabled={generating} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="generate-invite-button">
                {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Genera codice invito"}
              </Button>
              {lastCode && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-sky-500/10 border border-sky-500/30" data-testid="generated-invite-code">
                  <code className="flex-1 font-mono text-sm font-semibold truncate">{lastCode}</code>
                  <Button size="sm" variant="ghost" onClick={copyCode} data-testid="copy-invite-button">
                    {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              )}
              {lastCode && <p className="text-xs text-muted-foreground">Copia e condividi il codice: non sarà più visibile dopo aver lasciato questa pagina.</p>}
            </div>

            <div className="space-y-2">
              {invites.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">Nessun invito generato</p>}
              {invites.map((inv) => (
                <div key={inv.id} data-testid={`invite-row-${inv.id}`} className="flex items-center gap-3 p-3 rounded-xl border border-border bg-card">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{inv.email || "Invito generico"}</div>
                    <div className="text-xs text-muted-foreground">Scade {relativeSeen(inv.expiresAt)}</div>
                  </div>
                  <Badge className={`${STATUS_COLOR[inv.status]} hover:${STATUS_COLOR[inv.status]}`}>{inv.status}</Badge>
                  {inv.status === "attivo" && (
                    <Button size="icon" variant="ghost" className="text-destructive" onClick={() => revoke(inv.id)} data-testid={`revoke-invite-${inv.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="backup" className="space-y-6">
            <div className="p-4 rounded-xl border border-border bg-card space-y-4" data-testid="ftp-config-card">
              <h3 className="font-semibold flex items-center gap-2"><Server className="h-4 w-4" /> Backup automatico via FTP</h3>
              <p className="text-xs text-muted-foreground">Ogni notte, all'ora indicata (ora del server), viene generato uno ZIP di tutte le schede e caricato sul server FTP con nome <code>backup_relatio_AAAAMMGG_HHMMSS.zip</code>.</p>
              {ftp && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2 space-y-1.5">
                      <Label className="text-xs">Host</Label>
                      <Input placeholder="ftp.esempio.it" value={ftp.host} onChange={(e) => setFtpField("host", e.target.value)} data-testid="ftp-host-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Porta</Label>
                      <Input type="number" value={ftp.port} onChange={(e) => setFtpField("port", e.target.value)} data-testid="ftp-port-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Utente</Label>
                      <Input value={ftp.username} onChange={(e) => setFtpField("username", e.target.value)} data-testid="ftp-user-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Password</Label>
                      <Input type="password" placeholder={ftp.hasPassword ? "•••••• (invariata)" : "password"} value={ftp.password} onChange={(e) => setFtpField("password", e.target.value)} data-testid="ftp-pass-input" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2 space-y-1.5">
                      <Label className="text-xs">Cartella remota (facoltativa)</Label>
                      <Input placeholder="/backup/relatio" value={ftp.remoteDir} onChange={(e) => setFtpField("remoteDir", e.target.value)} data-testid="ftp-dir-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Ora notturna (0–23)</Label>
                      <Input type="number" min={0} max={23} value={ftp.hour} onChange={(e) => setFtpField("hour", e.target.value)} data-testid="ftp-hour-input" />
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <Switch checked={!!ftp.tls} onCheckedChange={(v) => setFtpField("tls", v)} data-testid="ftp-tls-switch" />
                      <Label className="text-xs">FTPS (TLS)</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={!!ftp.enabled} onCheckedChange={(v) => setFtpField("enabled", v)} data-testid="ftp-enabled-switch" />
                      <Label className="text-xs">Backup notturno attivo</Label>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3 pt-1">
                    <Button onClick={saveFtp} disabled={savingFtp} className="bg-sky-600 hover:bg-sky-700 text-white gap-2" data-testid="ftp-save-button">
                      {savingFtp ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva
                    </Button>
                    <Button onClick={runFtp} disabled={runningFtp || !ftp.host} variant="outline" className="gap-2" data-testid="ftp-run-button">
                      {runningFtp ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />} Esegui backup ora
                    </Button>
                  </div>
                  {ftp.lastRun && (
                    <div className="text-xs rounded-lg border border-border bg-muted/40 p-3" data-testid="ftp-last-status">
                      Ultimo backup: <span className="font-mono">{new Date(ftp.lastRun).toLocaleString("it-IT")}</span>
                      {" · "}
                      <span className={ftp.lastStatus === "ok" ? "text-green-600 font-semibold" : "text-red-600 font-semibold"}>
                        {ftp.lastStatus === "ok" ? "riuscito" : "errore"}
                      </span>
                      {ftp.lastFile && ftp.lastStatus === "ok" && <> · <span className="font-mono">{ftp.lastFile}</span></>}
                      {ftp.lastError && ftp.lastStatus !== "ok" && <div className="text-red-600 mt-1 break-words">{ftp.lastError}</div>}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="p-4 rounded-xl border border-border bg-card space-y-3" data-testid="restore-card">
              <h3 className="font-semibold flex items-center gap-2"><Upload className="h-4 w-4" /> Ripristino da backup</h3>
              <p className="text-xs text-muted-foreground">Carica un file <code>relatio_db.json</code> oppure lo ZIP di backup: le conversazioni e le note verranno ripristinate (sovrascritte per ID). Gli utenti mancanti vengono re-inseriti.</p>
              <label className="inline-flex">
                <input type="file" accept=".json,.zip,application/json,application/zip" className="hidden"
                  data-testid="restore-file-input"
                  onChange={(e) => { setRestoreFile(e.target.files?.[0] || null); e.target.value = ""; }} disabled={restoring} />
                <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium cursor-pointer border border-border hover:bg-muted ${restoring ? "opacity-60 pointer-events-none" : ""}`} data-testid="restore-trigger">
                  {restoring ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Scegli file e ripristina
                </span>
              </label>
            </div>
          </TabsContent>

          <TabsContent value="security" className="space-y-6">
            <div className="p-4 rounded-xl border border-border bg-card space-y-4" data-testid="security-card">
              <h3 className="font-semibold flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Blocco automatico</h3>
              <p className="text-xs text-muted-foreground">Vale per tutti gli utenti. Dopo l'inattività o il ritorno dall'app in background oltre i secondi indicati, la sessione si blocca e serve PIN o password.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Inattività prima del blocco (minuti)</Label>
                  <Input type="number" min={1} max={240} value={sec.idleMinutes}
                    onChange={(e) => setSec((p) => ({ ...p, idleMinutes: e.target.value }))} data-testid="sec-idle-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Background prima del blocco (secondi)</Label>
                  <Input type="number" min={0} max={3600} value={sec.backgroundSeconds}
                    onChange={(e) => setSec((p) => ({ ...p, backgroundSeconds: e.target.value }))} data-testid="sec-bg-input" />
                </div>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Switch checked={!!sec.requirePin} onCheckedChange={(v) => setSec((p) => ({ ...p, requirePin: v }))} data-testid="sec-require-pin-switch" />
                <Label className="text-xs">Richiedi il PIN a tutti gli utenti (chi non ce l'ha dovrà impostarlo al prossimo accesso)</Label>
              </div>
              <Button onClick={saveSec} disabled={savingSec} className="bg-sky-600 hover:bg-sky-700 text-white gap-2" data-testid="sec-save-button">
                {savingSec ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <AlertDialog open={!!confirmUser} onOpenChange={(o) => !o && setConfirmUser(null)}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare l'utente?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmUser ? `${confirmUser.displayName} (${confirmUser.email}) verrà rimosso definitivamente. Le sue schede dirette e le relative note saranno eliminate; verrà rimosso dai gruppi di lavoro. L'operazione non è reversibile.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-user">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doDelete(); }} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-user">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Elimina"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!resetPinUser} onOpenChange={(o) => !o && setResetPinUser(null)}>
        <AlertDialogContent data-testid="reset-pin-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Azzerare il PIN?</AlertDialogTitle>
            <AlertDialogDescription>
              {resetPinUser ? `Il PIN di ${resetPinUser.displayName} (${resetPinUser.email}) verrà rimosso. Al prossimo accesso potrà entrare con la password e, se il PIN è obbligatorio, dovrà impostarne uno nuovo.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-reset-pin">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doResetPin(); }} disabled={resettingPin} className="bg-amber-600 text-white hover:bg-amber-700" data-testid="confirm-reset-pin">
              {resettingPin ? <Loader2 className="h-4 w-4 animate-spin" /> : "Azzera PIN"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!restoreFile} onOpenChange={(o) => !o && !restoring && setRestoreFile(null)}>
        <AlertDialogContent data-testid="restore-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Ripristinare da backup?</AlertDialogTitle>
            <AlertDialogDescription>
              {restoreFile ? `Verrà ripristinato il file "${restoreFile.name}". Le schede e le note esistenti con lo stesso ID verranno sovrascritte. Gli utenti mancanti verranno re-inseriti. L'operazione non è reversibile.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoring} data-testid="cancel-restore">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doRestore(); }} disabled={restoring} className="bg-sky-600 text-white hover:bg-sky-700" data-testid="confirm-restore">
              {restoring ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ripristina"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
